import * as React from "react";
import { Modal, Button, Header, Message, Table, Icon } from "semantic-ui-react";
import {
  downloadTemplate,
  uploadFaults,
  extractUploadErrorMessage,
  MAX_UPLOAD_BYTES,
  BulkUploadValidationResult
} from "./bulkUploadApi";
import styles from "./BulkUpload.module.scss";

/**
 * Formats a byte count as a human-readable size, choosing bytes, KB or MB so a small
 * limit is never shown as a confusing fraction like "0.001953125 MB".
 */
const formatFileSize = (bytes: number): string => {
  const kb = 1024;
  const mb = 1024 * 1024;
  if (bytes >= mb) {
    return `${(bytes / mb).toFixed(2).replace(/\.?0+$/, "")} MB`;
  }
  if (bytes >= kb) {
    return `${(bytes / kb).toFixed(2).replace(/\.?0+$/, "")} KB`;
  }
  return `${bytes} bytes`;
};

export type BulkUploadDialogProps = {
  /** The domain group this dialog targets. Inserts only ever go to this group. */
  domainGroup: Api.GroupName;
  /** The current user (reserved for permission-aware actions). */
  user: Api.User;
  /** `true` if the dialog is open. */
  isOpen: boolean;
  /** Called when the dialog is closed without uploading. */
  onClose: () => void;
};

/**
 * Bulk Upload dialog.
 *
 * Offers a *Download template* action (fetches the group's `.xlsx` template and
 * triggers a browser download) and an *Upload file* action (an `.xlsx` file
 * picker + submit that POSTs the file to the upload endpoint). After a submit,
 * the returned {@link BulkUploadValidationResult} is rendered as a summary line
 * and, when rows are rejected, a table of `{ rowPosition, reason }` (each
 * rejected row's reasons are flattened to one row per reason).
 *
 * Closing the dialog without uploading performs no network call and leaves fault
 * data untouched.
 */
const BulkUploadDialog: React.FC<BulkUploadDialogProps> = ({ domainGroup, isOpen, onClose }) => {
  const [file, setFile] = React.useState<File | null>(null);
  const [result, setResult] = React.useState<BulkUploadValidationResult | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isDownloading, setIsDownloading] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  // True once an upload has actually persisted rows (result.inserted === true).
  // Used to disable the submit button so the same file cannot be double-submitted.
  const [uploadSucceeded, setUploadSucceeded] = React.useState(false);

  /** Resets all transient dialog state and closes without any mutation. */
  const handleClose = (): void => {
    setFile(null);
    setResult(null);
    setIsSubmitting(false);
    setIsDownloading(false);
    setErrorMessage(null);
    setUploadSucceeded(false);
    onClose();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const selected = event.target.files && event.target.files[0] ? event.target.files[0] : null;
    setFile(selected);
    // A new file selection invalidates any previously shown result and re-enables
    // submit so a different file can be uploaded.
    setResult(null);
    setErrorMessage(null);
    setUploadSucceeded(false);
  };

  const handleDownloadTemplate = async (): Promise<void> => {
    setErrorMessage(null);
    setIsDownloading(true);
    try {
      await downloadTemplate(domainGroup);
    } catch (e) {
      setErrorMessage(`Failed to download the ${domainGroup} template.`);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSubmit = async (): Promise<void> => {
    if (!file) {
      return;
    }
    // Clear any previous outcome so a new attempt never shows a stale success message
    // stacked alongside a new error (or vice versa).
    setErrorMessage(null);
    setResult(null);

    // Client-side size guard mirrors the API's cap so an oversized file is caught
    // before any upload request is made.
    if (file.size > MAX_UPLOAD_BYTES) {
      setErrorMessage(
        `The selected file (${formatFileSize(file.size)}) exceeds the maximum allowed size of ` +
          `${formatFileSize(MAX_UPLOAD_BYTES)}. Please upload a smaller .xlsx file.`
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const validationResult = await uploadFaults(domainGroup, file);
      setResult(validationResult);
      // Only a persisted insert disables submit; a rejected result lets the user retry.
      if (validationResult.inserted) {
        setUploadSucceeded(true);
      }
    } catch (e) {
      // Surface the backend's specific acceptance message (wrong file, empty, too large,
      // missing columns) when present; otherwise a generic failure sentence.
      setErrorMessage(
        extractUploadErrorMessage(
          e,
          `The uploaded fault template is invalid or incorrect for the ${domainGroup} domain group. ` +
            "Please download the template, fill it in, and try again."
        )
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  /** Flattens each rejected row into one display row per reason. */
  const flattenedRejections = result
    ? result.rejectedRows.reduce<{ rowPosition: number; reason: string }[]>((acc, rejected) => {
        rejected.reasons.forEach(reason => acc.push({ rowPosition: rejected.rowPosition, reason }));
        return acc;
      }, [])
    : [];

  return (
    <Modal
      open={isOpen}
      size="small"
      data-testid="bulk-upload-dialog"
      onClose={handleClose}
      className={styles.dialog}
    >
      <Header className={styles.header} data-testid="bulk-upload-dialog-header">
        <span className={styles.headerTitle}>{`Bulk Upload - ${domainGroup}`}</span>
        <Icon
          name="close"
          link={true}
          className={styles.headerClose}
          data-testid="bulk-upload-dialog-close-icon"
          onClick={handleClose}
          aria-label="Close"
        />
      </Header>
      <Modal.Content className={styles.content}>
        <p>{`Bulk upload faults for the ${domainGroup} domain group.`}</p>

        <Message info={true} className={styles.instructions} data-testid="bulk-upload-instructions">
          <Message.Header>Before you upload</Message.Header>
          <Message.List>
            <Message.Item>
              Download the template below and fill in one fault per row. Required columns are marked
              with an asterisk (*).
            </Message.Item>
            <Message.Item>
              Refer to the fault field definitions in KCEDB for the expected input criteria and
              format of each fault-related field.
            </Message.Item>
            <Message.Item>
              Only <strong>.xlsx</strong> files are accepted, up to {formatFileSize(MAX_UPLOAD_BYTES)}.
              Rows are inserted only when every row is valid.
            </Message.Item>
          </Message.List>
        </Message>

        <Button
          data-testid="bulk-upload-download-template"
          icon={true}
          labelPosition="left"
          onClick={handleDownloadTemplate}
          loading={isDownloading}
          disabled={isDownloading}
        >
          <Icon name="download" />
          Download template
        </Button>

        <div className={styles.fileInput}>
          <input
            data-testid="bulk-upload-file-input"
            type="file"
            accept=".xlsx"
            onChange={handleFileChange}
          />
        </div>

        {errorMessage && (
          <Message negative={true} data-testid="bulk-upload-error">
            {errorMessage}
          </Message>
        )}

        {result && (
          <Message
            positive={result.inserted}
            negative={!result.inserted}
            className={result.inserted ? styles.summarySuccess : styles.summaryRejected}
            data-testid="bulk-upload-result-summary"
          >
            <Message.Header>
              {result.inserted ? "Upload succeeded" : "Upload rejected"}
            </Message.Header>
            <p>
              {`Inserted: ${result.insertedCount}, Rejected: ${result.rejectedCount}`}
            </p>
          </Message>
        )}

        {result && result.rejectedCount > 0 && (
          <Table
            celled={true}
            compact={true}
            striped={true}
            className={styles.rejectedTable}
            data-testid="bulk-upload-rejected-table"
          >
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Row</Table.HeaderCell>
                <Table.HeaderCell>Reason</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {flattenedRejections.map((rejection, index) => (
                <Table.Row
                  key={`${rejection.rowPosition}-${index}`}
                  data-testid="bulk-upload-rejected-row"
                >
                  <Table.Cell>{rejection.rowPosition}</Table.Cell>
                  <Table.Cell>{rejection.reason}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Modal.Content>
      <Modal.Actions className={styles.actions}>
        <Button
          data-testid="bulk-upload-submit"
          primary={true}
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={!file || isSubmitting || uploadSucceeded}
        >
          Upload
        </Button>
        <Button data-testid="bulk-upload-dialog-close" onClick={handleClose}>
          Close
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default BulkUploadDialog;
